/**
@format
@flow
*/
import * as React from 'react';
import styled from 'styled-components';
import Colors from '@ziynx/theme/colors';
import { ButtonPrimary as KrispyPrimary } from '@krispy/buttons/web';
import { getDimensions, LoadingSpinner } from '@ziynx/components/utilities';

export type Props = $ReadOnly<{|
  /**
   * Disabled treatement (both visually and functionally) for our Primary button.
   */
  disabled?: boolean,

  /**
   * If button is inverted. Background is White
   */
  inverted?: boolean,

  /**
   * Depicts when button is in loading state
   */
  isLoading?: boolean,

  /**
  * Text for the button
  */
  children?: any,

  /**
  * Set a custom width for button.
  */
  width?: string | number,
  /**
  * Set a custom height for button.
  */
  height?: string | number,

  /**
   * BackgroundColor for button, if any
   * Defaults to PrimaryBlue if active and GrayVeryLight if disabled
   */
  backgroundColor?: string,

  /**
  * TextColor for button, if any
  * Defaults to Black
  */
  textColor?: string,

  /**
   * Border color if hasBorder is true
   * Defaults to GrayDark
   */
  borderColor?: string,

  /**
  * Set a function to fire when the button is clicked.
  */
  onClick?: (event: SyntheticEvent<HTMLButtonElement>) => any | Promise<any>;

  /**
   * Controls whether button uses width or min-width flex responsiveness
  */
 minWidth?: string | number,
|}>;

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ButtonPrimary = (props: Props): React$Node => {
  const {
    children,
    disabled,
    inverted,
    isLoading,
    borderColor,
    backgroundColor,
    onClick,
    textColor,
    height,
    width,
    minWidth,
    ...rest
  } = props;

  const findBackgroundColor = (isHovering): string => {
    if (inverted || (inverted && disabled)) {
      return Colors.White;
    } if (disabled) {
      return Colors.GrayLight;
    } if (isLoading) {
      return Colors.BrandBlueFaded;
    } if (isHovering) {
      return Colors.BrandBlueDark;
    }
    return Colors.PrimaryBlue;
  };

  const findTextColor = (): string => {
    if (inverted && !disabled && !isLoading) {
      return Colors.GrayDark;
    } if ((inverted && disabled) || (inverted && isLoading)) {
      return Colors.GrayLight;
    }
    return Colors.White;
  };

  const findBorderColor = (): string => {
    if (inverted && !disabled && !isLoading) {
      return Colors.GrayDark;
    } if ((inverted && disabled) || (inverted || isLoading)) {
      return Colors.GrayLight;
    }
    return '';
  };

  const heightVar = getDimensions(height);
  const widthVar = getDimensions(width);
  const minWidthVar = getDimensions(minWidth);

  return (
    <KrispyPrimary
      disabled={disabled || isLoading}
      inverted={inverted}
      isLoading={isLoading}
      backgroundColor={backgroundColor}
      findBackgroundColor={findBackgroundColor}
      textColor={textColor || findTextColor()}
      borderColor={borderColor || findBorderColor()}
      onClick={onClick}
      colorConfig={Colors}
      height={heightVar}
      width={widthVar}
      minWidth={minWidthVar}
      {...rest}
    >
      {!isLoading ? children : (
        <LoadingSpinnerWrapper>
          <LoadingSpinner color={inverted ? Colors.GrayLight : Colors.White} />
        </LoadingSpinnerWrapper>
      )}
    </KrispyPrimary>
  );
};

ButtonPrimary.defaultProps = {
  onClick: (): void => {},
  width: '200px',
  minWidth: undefined,
  height: '46px',
  disabled: false,
  isLoading: false,
  inverted: false,
};

export default ButtonPrimary;
